import { NgClass } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  NgZone,
  AfterViewInit,
  HostListener,
} from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SplitComponent, SplitAreaComponent } from 'angular-split';
import { PDFDocumentProxy } from 'ng2-pdf-viewer';
import { NgxExtendedPdfViewerService } from 'ngx-extended-pdf-viewer';
import { finalize, Subject, takeUntil } from 'rxjs';

import { EditDocFormSectionContentComponent } from './edit-doc-form-section-content/edit-doc-form-section-content.component';
import { EditDocChangesConfirmComponent } from '../../components/edit-doc-changes-confirm/edit-doc-changes-confirm.component';
import { ExtendedPdfViewerComponent } from '../../components/pdf-viewer/extended-pdf-viewer/extended-pdf-viewer.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { StyledButtonComponent } from '../../components/styled-button/styled-button.component';
import { ICustomCSS } from '../../interfaces/custom-css.interface';
import {
  IEditDocumentSingleFormField,
  IDocumentInfoForEditDoc,
} from '../../interfaces/edit-document.interface';
import { IConfirmModalConfig } from '../../interfaces/modal-dialog-config.interface';
import {
  EditDocChangesConfirm,
  ModalData,
} from '../../interfaces/modal-dialog-service-data-injection.interface';
import { IGetPdfFormFieldsParams } from '../../interfaces/pdf-populate.interface';
import { Acp4Api } from '../../services/acp4-api/acp4-api.service';
import { CheckForAuditsService } from '../../services/audits/check-for-audits/check-for-audits.service';
import { ModalDialogService } from '../../services/modalDialog/modal-dialog.service';
import { PdfPopulateService } from '../../services/pdf-populate/pdf-populate.service';
import { PdfViewerSrcManagerService } from '../../services/pdf-viewer-src-manager/pdf-viewer-src-manager.service';
import { ToastNotificationsManagerService } from '../../services/toast-notifications-manager/toast-notifications-manager.service';
import { AddToastNotif } from '../../types/toast-notification.type';
import { default as wizards } from '../wizards/form-wizard-definitions';
import { WizardComponent } from '../wizards/wizard/wizard.component';

@Component({
  selector: 'app-edit-document',
  templateUrl: './edit-document.component.html',
  styleUrls: ['./edit-document.component.scss'],
  imports: [
    SplitComponent,
    SplitAreaComponent,
    ExtendedPdfViewerComponent,
    NgClass,
    SpinnerComponent,
    ReactiveFormsModule,
    FormsModule,
    EditDocFormSectionContentComponent,
    StyledButtonComponent,
    WizardComponent
],
})
export class EditDocumentComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
  @ViewChild('wizard', {
    static: false,
  })
  wizard: WizardComponent;
  @ViewChild('pdfViewer') pdfViewer: ExtendedPdfViewerComponent;
  /*splitEl refers to the center piece of the edit document page (the part that splits the pdf side and the document editing side).*/
  @ViewChild('splitEl') splitEl: SplitComponent;

  private _onDestroy = new Subject<void>();
  sseObserver: any;
  messageTypes: string[] = ['auditResolved'];
  /* combined data for the page */
  summaryData: any = [];
  originalSummaryData: any = [];
  fieldValues: any = {};
  initialFieldValues: any = {};
  // TODO: summaryData should be used instead of fetchingFeilds. Summary Data should also be renamed.
  // fetchingFeilds may be unecessary because we don't need just blank field names, we need mapping,
  // values, validations, regex, dependencies
  fetchingFields = false;

  fetchingAudits = false;
  fetchingDocumentName = false;
  pdfLoadComplete = false;
  searchInput: any;
  blankFields = false;
  changedFields = false;
  invalidFields = false;
  searchResults: boolean;
  allSelected = false;
  filterOptions: any[] = [
    {
      name: 'Blank Fields',
      checked: false,
    },
    {
      name: 'Invalid Fields',
      checked: false,
    },
    {
      name: 'Changed Fields',
      checked: false,
    },
  ];

  lettersRegex = /[a-zA-Z]/;
  numbersRegex = /\d/;
  specialsRegex = /[`!@#$%^&*()_+=[\]{};':"\\|,.<>/?~]/;
  disableEditing = false;
  auditPending = false;
  auditSubmitted = false;
  panelOpenState = true;
  changesMade = false;
  changes: any = {};
  changesKeys: Array<any> = Object.keys(this.changes);
  auditNote = '';
  inputIdHighlightActive = '';
  activeWizard: any = undefined;
  wizards = wizards;
  currentTabAll = true;
  /* 
    gutterPositionArray consists of two values which are the area left and right of the gutter; 
    ".sizes[0]" to the left and ".sizes[1]" to the right.
    gutterPositionValue is the trimmed value which currently is assigned to ".sizes[0]" We only use one for the values, 
    as the area remaining can always be calculated by using that one value (100 - .sizes[0] = remaining size
  */
  gutterPositionArray: any = [];
  gutterPositionValue: any;

  activatePdfViewer = false;
  returnLink: string;
  documentInfo: IDocumentInfoForEditDoc;

  @HostListener('click', ['$event'])
  clickHandler(event: any) {
    const validElements = ['TEXTAREA', 'INPUT'];
    const el = event.srcElement.tagName;
    // Checking if the target element clicked is an INPUT or TEXTAREA,
    // and if it is not, it will remove the focus highlight.
    if (!validElements.includes(el)) this.inputIdHighlightActive = '';
  }
  @HostListener('window:keyup', ['$event'])
  tabKeyCheck(event: any) {
    const validElements = ['TEXTAREA', 'INPUT'];
    const el = event.srcElement.tagName;
    const isInput = validElements.includes(el);
    // Checking if the target element tabbed to is an INPUT, if the keyboard click was Tab, and if the target is valid AND editable
    // If all conditions are met, the tabbed to element is highlighted
    if (event.keyCode == 9) if (isInput && event?.srcElement) this.setActiveInputElement(event);
  }

  constructor(
    public activatedRoute: ActivatedRoute,
    private apiSvc: Acp4Api,
    private checkAuditsService: CheckForAuditsService,
    private cdRef: ChangeDetectorRef,
    private modalService: ModalDialogService,
    private toastNotif: ToastNotificationsManagerService,
    private router: Router,
    private pdfSrcService: PdfViewerSrcManagerService,
    public pdfPopulateService: PdfPopulateService,
    private ngZone: NgZone,
    private ngxService: NgxExtendedPdfViewerService,
  ) {}

  /**
   * handleFormDataChange()
   * accepts the e from any instance that the edit document form change event is triggered
   * and will update the pdf viewer annotations with the new value
   */
  async handleFormDataChange() {
    this.populatePdf();
    this.pdfPopulateService.populateFormData();
    await this.ngxService.getFormData();
  }

  /* extractRouteParamesters() will get params from url and get details for doc editing */
  extractRouteParameters() {
    const extractedParams: any = {};
    this.activatedRoute.queryParams.subscribe(params => {
      Object.keys(params).forEach(key => {
        if (key === 'Reference') this.returnLink = params.Reference;
        extractedParams[key] = parseInt(params[key]);
      });
      if (!extractedParams.SystemID || !extractedParams.DocumentID || !this.returnLink) {
        const notification: AddToastNotif = {
          type: 'error',
          content: 'The url is invalid, you have been returned to the bo documents components',
        };
        this.toastNotif.addNew(notification);
        this.router.navigate(['/forms']);
      } else {
        this.activatePdfViewer = true;
        this.getFormFields(extractedParams);
      }
    });
  }

  /* getFormFields() fill fetch the data to populate form and PDF */
  getFormFields({ SystemID, DocumentID }: { SystemID: number; DocumentID: number }) {
    /* TODO: Handle no IDs for get form fields API call */
    this.summaryData = this.originalSummaryData = [];
    this.fieldValues = this.initialFieldValues = {};
    this.fetchingFields = true;
    const params: IGetPdfFormFieldsParams = {
      SystemID,
      DocumentID,
    };
    this.pdfPopulateService.getFormFieldsData(params).subscribe((response: any) => {
      const { results: formFieldsData }: { results: IEditDocumentSingleFormField[] } = response;

      this.pdfPopulateService.formFieldsData = formFieldsData;
      this.documentInfo = {
        SystemID,
        DocumentID,
        BeneficialOwnerID: Number(formFieldsData[0].BeneficialOwnerID),
        DocumentTypeID: Number(formFieldsData[0].DocumentTypeID),
        BeneficialOwnerTypeID: Number(formFieldsData[0].BeneficialOwnerTypeID),
      };
      this.pdfSrcService.getPdfDocumentTypeBinary(this.documentInfo.DocumentTypeID);
      this.getPendingAudits();
      this.getDocumentName(SystemID, DocumentID);
      this.fetchingFields = false;
    });
  }

  /** getPendingAudits()
   * POST request to reutn only pending audits ("AuditApprovalStatusID": 1) for
   * required SystemID, Bo DocumentID, DocumentTypeID, and
   * optional BeneficialOwnerID, AuditTypeID, or isIncludeDeleted (boolean)
   */
  getPendingAudits() {
    this.fetchingAudits = true;
    this.disableEditing = true;
    const params: any = {
      SystemID: this.documentInfo.SystemID,
      DocumentTypeID: this.documentInfo.DocumentTypeID,
      DocumentID: this.documentInfo.DocumentID,
      AuditApprovalStatusID: 1,
    };
    this.checkAuditsService
      .getAudits(params)
      .pipe(takeUntil(this._onDestroy))
      .subscribe(response => {
        this.handleFormFieldsData();
        if (response.results.length) this.auditPending = true;
        else this.disableEditing = false;
        this.fetchingAudits = false;
      });
  }

  /* handleFormFieldsData() will call function to transform formFields from DB */
  handleFormFieldsData() {
    this.pdfPopulateService.transformData(this.pdfPopulateService.formFieldsData);
    this.summaryData = this.pdfPopulateService.summaryData;
    this.originalSummaryData = this.pdfPopulateService.originalSummaryData;
    this.fieldValues = this.pdfPopulateService.fieldValues;
    this.initialFieldValues = this.pdfPopulateService.initialFieldValues;
    this.pdfPopulateService.formFieldsData =
      []; /* formFieldsData is a large object - remove for performance */
  }

  /**
   * getDocumentName()
   * will query the db to find the name of the form being accessed
   * to determine if there is a wizard associated with that form and investor type
   * @param SystemID
   * @param DocumentID
   */
  getDocumentName(SystemID: number, DocumentID: number) {
    this.fetchingDocumentName = true;
    const params = { SystemID, DocumentID };
    this.apiSvc
      .post(this.apiSvc.URL_BO_DOCUMENT_NAME, params)
      .pipe(finalize(() => (this.fetchingDocumentName = false)))
      .subscribe({
        next: res => {
          if (res?.results instanceof Array)
            this.documentInfo.DocumentTypeName = res.results[0]?.DocumentTypeName;
        },
        error: err => this.apiSvc.defaultErrResHandler(err),
      });
  }

  /**
   * isExternal()
   * @param url
   * @returns
   */
  isExternal(url: string) {
    const match = /^([^:/?#]+:)?(?:\/\/([^/?#]*))?([^?#]+)?(\?[^#]*)?(#.*)?/.exec(url);
    if (!match) return false;
    if (
      typeof match[1] === 'string' &&
      match[1].length > 0 &&
      match[1].toLowerCase() !== location.protocol
    )
      return true;
    if (
      typeof match[2] === 'string' &&
      match[2].length > 0 &&
      match[2].replace(
        new RegExp(
          ':(' +
            {
              'http:': 80,
              'https:': 443,
            }[location.protocol] +
            ')?$',
        ),
        '',
      ) !== location.host
    )
      return true;
    return false;
  }

  /**
   * setActiveInputElement() will set or remove currently selected input based on focus
   * @param clickedInput is the component that registered the click
   */
  setActiveInputElement(clickedInput?: MouseEvent) {
    if (!clickedInput) return (this.inputIdHighlightActive = '');
    const target = clickedInput.target as HTMLInputElement;
    const { id } = target;
    return (this.inputIdHighlightActive = id.replace('Right-', ''));
  }

  /**
   * detectActiveInputElement()
   * triggered by checkbox input click on PDF
   * @param activeInputElementID
   */
  detectActiveInputElement(activeInputElementID: any): void {
    this.inputIdHighlightActive = activeInputElementID;
  }

  /**
   * check if string contain numbers
   * @param str to be validated
   * @returns boolean; true if string contain any regex value
   */
  validateForNumbers(str: string) {
    return this.numbersRegex.test(str);
  }

  /**
   * check if string contain special characters
   * @param str to be validated
   * @returns boolean; true if string contain any regex value
   */
  validateForSpecials(str: string) {
    return this.specialsRegex.test(str);
  }

  /**
   * check if string contain a-z letters
   * @param str to be validated
   * @returns boolean; true if string contain any regex value
   */
  validateForLetters(str: string) {
    return this.lettersRegex.test(str);
  }

  /**
   * Determines the error message that will display when invalid input is entered into a field.
   * @param fieldName
   * @returns
   */
  invalidMessage(fieldName: string): string {
    const field = this.fieldValues[fieldName];
    if (field.forbidSpecials && field.forbidNumbers)
      return 'Field cannot contain numbers or special characters.';
    else if (field.forbidSpecials && field.forbidLetters)
      return 'Field cannot contain letters or special characters.';
    else if (field.forbidLetters) return 'Field cannot contain letters.';
    else if (field.forbidNumbers) return 'Field cannot contain numbers.';
    else if (field.forbidSpecials) return 'Field cannot contain special characters.';

    return '';
  }

  /**
   * find the field by its name in this.fieldValue and validate its content (data)
   * invalid fields will have invalid property set to true or removed if valid
   * @param fieldName to be validated
   */
  validateInput(fieldName: string) {
    if (this.fieldValues[fieldName]?.forbidSpecials) {
      const invalid = this.validateForSpecials(this.fieldValues[fieldName].DataValue);
      if (invalid) {
        this.fieldValues[fieldName].invalid = true;
        return;
      }
    }
    if (this.fieldValues[fieldName]?.forbidNumbers) {
      const invalid = this.validateForNumbers(this.fieldValues[fieldName].DataValue);
      if (invalid) {
        this.fieldValues[fieldName].invalid = true;
        return;
      }
    }
    if (this.fieldValues[fieldName]?.forbidLetters) {
      const invalid = this.validateForLetters(this.fieldValues[fieldName].DataValue);
      if (invalid) {
        this.fieldValues[fieldName].invalid = true;
        return;
      }
    }
    if (
      this.fieldValues &&
      this.fieldValues[fieldName]?.requirement === 'required' &&
      !this.fieldValues[fieldName]?.DataValue?.trim().length
    ) {
      this.fieldValues[fieldName].invalid = true;
      return;
    }
    delete this.fieldValues[fieldName]?.invalid;
  }

  /* populatePdf() will update the pdf inputs values and styling with the data saved in fieldValues */
  populatePdf() {
    for (const field in this.fieldValues) {
      const fieldObj = this.fieldValues[field];
      const id = fieldObj.FormFieldName;
      for (let i in id) {
        i = id[i];
        const element = document.getElementById(i) as HTMLInputElement;
        if (element) {
          element.value = this.fieldValues[fieldObj.DataFieldDefinitionID].DataValue;
          if (fieldObj.DataTypeName === 'BIT')
            element.checked = this.fieldValues[fieldObj.DataFieldDefinitionID].DataValue;

          // TODO: please look up pdf.js error - "Warning: Annotation is missing the required /Subtype."
          // TODO: please look up pdf.js error - "Warning: Knockout groups not supported."

          /** this if statement applies CustomCSS property value to input elements from the BO Form Template JSON */
          if (this.fieldValues[fieldObj.DataFieldDefinitionID].CustomCSS) {
            // NOTE: of inputs 156R-166R, 172R
            // lightpink background to indicate parents which have been affected
            // red border to indicate 'landscape' element
            // parent.style.background = "lightpink";
            // element.style.border = "1px solid red";

            // TODO: 1) unhash the string in CustomCSS to obtain the css and remove the temporary lines below
            const customCSS: ICustomCSS = JSON.parse(
              this.fieldValues[fieldObj.DataFieldDefinitionID].CustomCSS,
            );
            // TODO: define ICustomCSS to be iterable so that we can extend to more CSS properties
            // for(const prop of customCSS) {
            //   if(Object.hasOwnProperty(prop)){
            //     element.style[prop] = `${customCSS[prop]}`;
            //   }
            // }
            element.style.width = customCSS?.width ? `${customCSS?.width}` : element.style.width;
            element.style.height = customCSS?.height
              ? `${customCSS?.height}`
              : element.style.height;
            element.style.transform = customCSS?.transform
              ? `${customCSS?.transform}`
              : element.style.transform;
            element.style.display = customCSS?.display
              ? `${customCSS?.display}`
              : element.style.display;
          }
        }
      }
    }
  }

  /**
   * handleAgreeAndSaveClick() activates a modal with a change receipt
   * upon click of the Agree and Save button
   */
  handleAgreeAndSaveClick() {
    const modalConfig: ModalData.Config<EditDocChangesConfirm> = {
      cssClass: 'modal-lg',
      content: {
        component: EditDocChangesConfirmComponent,
        configData: {
          changes: Object.keys(this.changes).map(key => ({
            ReferenceColumnName: this.changes[key].DataFieldName,
            DataFieldName: this.changes[key].DataFieldName,
            DataFieldDefinitionID: this.changes[key].DataFieldDefinitionID,
            ReferenceDataID: this.changes[key].DocumentDataID,
            DataValueNew: this.changes[key].DataValue,
            prevValue: this.initialFieldValues[key].DataValue,
          })),
          documentInfo: this.documentInfo,
        },
      },
    };
    this.modalService
      .showCustomCompModal<EditDocChangesConfirmComponent>(modalConfig)
      .content?.modalResponse.subscribe(message => {
        switch (message) {
          case 'Edit Doc Audit Pending':
            this.auditPending = this.disableEditing = true;
            break;
          case 'Edit Doc Audit Created':
            {
              this.auditPending = this.disableEditing = this.auditSubmitted = true;
              this.changesKeys = [];
              const notification: AddToastNotif = {
                type: 'success',
                content: 'Successfully created audit(s).',
              };
              if (this.isExternal(this.returnLink))
                // External Origin
                this.router.navigateByUrl(this.returnLink);
              else {
                // Internal Origin
                const internalRoute = [this.returnLink];
                this.toastNotif.addNew(notification);
                this.router.navigate(internalRoute);
              }
            }
            break;
          default:
            break;
        }
      });
    // TOOD: if Cancel Edit (not cancel change in this modal), then we need to return to origin
  }

  /**
   * agreeAndSaveCheck() is in the template and is being evaluated on scroll, keypress, AND every event!
   * We need to make this performant! -->
   * determines if agree and save button is clickable
   * checks for changes
   * checks for whether or not editing is enabled
   * checks for all required fields filled out and validated
   * TODO: eventually will check for signature as well
   * @returns true if there are changes made
   */
  agreeAndSaveCheck() {
    if (this.auditSubmitted || this.auditPending || this.disableEditing) return true;

    this.changes = {};
    let buttonDisabled = false;

    for (const key in this.fieldValues) {
      const DataTypeName = this.fieldValues[key]?.DataTypeName;
      const DataValue =
        DataTypeName == 'STRING' && typeof this.fieldValues[key]?.DataValue == 'string'
          ? this.fieldValues[key]?.DataValue?.trim()
          : this.fieldValues[key]?.DataValue;
      const InitialValue = this.initialFieldValues[key]?.DataValue;

      if (
        this.fieldValues[key]?.requirement === 'required' &&
        !this.fieldValues[key]?.DataValue?.trim().length
      )
        buttonDisabled = true;

      if (this.fieldValues[key]?.invalid) buttonDisabled = true;

      if (
        ((DataTypeName == 'STRING' || DataTypeName == 'DATE') &&
          DataValue != InitialValue &&
          (DataValue?.length || InitialValue)) ||
        (DataTypeName == 'BIT' && DataValue != InitialValue)
      )
        // TODO: this assignment of keys in this.changes. If keys has special characters then everything downstream may break.
        this.changes[key] = {
          DataValue: DataValue,
          DataFieldName: this.fieldValues[key].DataFieldName,
          DataFieldDefinitionID: this.initialFieldValues[key].DataFieldDefinitionID,
          DocumentDataID: this.initialFieldValues[key].DocumentDataID,
        };
    }
    this.changesKeys = Object.keys(this.changes);
    if (this.changesKeys?.length < 1) buttonDisabled = true;
    return buttonDisabled;
  }

  /**
   * handleTabClick() will determine whether all fields or wizard is shown
   * @param tab
   */
  handleTabClick(tab: string) {
    /* TODO: Refactor this if-else hell */
    if (tab == 'all') {
      if (!this.currentTabAll)
        if (this.wizard?.changes && Object.keys(this.wizard?.changes)?.length) {
          const confirmConfig: IConfirmModalConfig = {
            title: 'Confirm leave',
            message: 'Are you sure you would like to view All Fields? You will lose your changes.',
            confirmBtnLabel: 'Confirm',
            confirmBtnCssClass: 'btn btn-danger px-sm-4',
            cancelBtnLabel: 'Cancel',
          };
          this.modalService.showConfirmModal(confirmConfig)?.subscribe(confirm => {
            if (confirm) {
              this.wizard.startOver(true);
              this.currentTabAll = true;
            }
          });
        } else this.currentTabAll = true;
    } else if (tab == 'wizard')
      if (this.auditPending || this.auditSubmitted || this.disableEditing)
        this.currentTabAll = true;
      else if (this.changesKeys.length) {
        const modalConfig: IConfirmModalConfig = {
          title: 'Confirm leave',
          message: 'Are you sure you would like to view the Wizard? You will lose your changes.',
          confirmBtnLabel: 'Confirm',
          confirmBtnCssClass: 'btn btn-danger px-sm-4',
          cancelBtnLabel: 'Cancel',
        };
        this.modalService.showConfirmModal(modalConfig)?.subscribe(confirm => {
          if (confirm) {
            this.removeAllChanges();
            this.currentTabAll = false;
          }
        });
      } else this.currentTabAll = false;
  }

  /**
   * mapWizardFields accepts the event emitted from the wizard.component
   * and iterates through the object values to map them to the All Fields view once wizard is submitted
   * @param e
   */
  mapWizardFields(e: any) {
    this.currentTabAll = true;

    for (const field in this.fieldValues) {
      const fieldIDs = this.fieldValues[field].FormFieldName;
      for (const fieldID of fieldIDs)
        if (e[fieldID]) this.fieldValues[field].DataValue = e[fieldID];
    }
  }

  /**
   * removeAllChanges() will reset all changes made by the user
   */
  removeAllChanges() {
    for (const section in this.summaryData) {
      const currentSection = this.summaryData[section];
      for (const field in currentSection[1])
        if (this.changesKeys.includes(field.toString())) {
          const originalValue = this.initialFieldValues[field].DataValue;
          this.fieldValues[field].DataValue = originalValue;
        }
    }
    this.inputIdHighlightActive = '';
    this.setActiveInputElement();
    this.detectActiveInputElement('');
    this.changes = {};
    this.changesKeys = [];
    this.changesMade = false;
  }

  /**
   * checkForWizard()
   * @returns whether or not a wizard exists for the documentInfo.DocumentTypeID
   * TODO: this data should somehow be stored and fetched from backend in future ticket
   */
  checkForWizard(): boolean {
    const activeWizard = Object.keys(this.wizards).find(
      key =>
        this.wizards[key].DocumentTypeName === this.documentInfo?.DocumentTypeName &&
        this.wizards[key].investorType === this.documentInfo?.BeneficialOwnerTypeID,
    );
    if (activeWizard) {
      this.activeWizard = activeWizard;
      this.wizards[this.activeWizard].investorType = this.documentInfo.BeneficialOwnerTypeID;
      return true;
    }
    return false;
  }

  /**
   * filterSearch takes in user input, loops through keyvalue pairs in fieldValues.
   * @returns results that match with user input.
   */
  filterSearch() {
    /* TODO: Refactor this if-else hell */
    if (this.searchInput?.length) {
      this.summaryData = [];
      this.searchResults = true;

      for (const sectionArr in this.originalSummaryData) {
        const sectionName = this.originalSummaryData?.[sectionArr][0].sectionName;
        const section: any = this.originalSummaryData?.[sectionArr][1];
        const filteredSection: any = {};

        for (const key in section) {
          const field: any = section[key];
          if (
            field?.DataFieldName?.toLowerCase().includes(this.searchInput?.toLowerCase().trim()) ||
            (field?.DataTypeName == 'STRING' &&
              field?.DataValue?.toLowerCase()
                .trim()
                .includes(this.searchInput.toLowerCase().trim()))
          )
            if (this.anySelected()) {
              if (this.checkboxCheck(field)) filteredSection[key] = field;
            } else filteredSection[key] = field;
        }
        if (Object.keys(filteredSection)?.length)
          this.summaryData.push([sectionName, filteredSection]);
      }
    } else if (this.anySelected()) {
      this.summaryData = [];
      this.searchResults = true;

      for (const section of this.originalSummaryData) {
        const filteredSection: any = {};
        for (const key in section[1]) {
          const field = section[1][key];

          if (this.checkboxCheck(field)) filteredSection[key] = field;
        }

        if (Object.keys(filteredSection)?.length)
          this.summaryData.push([section[0].sectionName, filteredSection]);
      }
    } else {
      this.searchResults = false;
      this.summaryData = [...this.originalSummaryData];
    }
  }

  /**
   * anySelected checks if there are any checkbox filters selected
   * @returns bool
   */
  anySelected(): boolean {
    let anySelected = false;
    for (const filter of this.filterOptions) if (filter.checked) anySelected = true;

    return anySelected;
  }

  /**
   * checkboxCheck returns fields that match any of the selected checkbox filters
   * or not if it maches none
   * This is only called when checkboxes are selected
   * TODO: Fix "Changed Fields" as it does not show any changed fields on the FE.
   */
  checkboxCheck(field: any): boolean {
    const DataFieldDefinitionID = field.DataFieldDefinitionID;
    const DataValue = field?.DataValue;

    for (const filter of this.filterOptions) {
      if (filter.name === 'Blank Fields' && filter.checked) if (!DataValue?.length) return true;

      if (filter.name === 'Invalid Fields' && filter.checked)
        if (this.fieldValues[DataFieldDefinitionID]?.invalid) return true;

      if (filter.name === 'Changed Fields' && filter.checked)
        if (this.changesKeys.includes(DataFieldDefinitionID?.toString())) return true;
    }
    return false;
  }

  /**
   * toggleAllSelection registers the change in value when checkbox goes through an event
   * and returns all checkboxes as true/false upon click.
   */
  toggleAllSelection() {
    if (this.allSelected) this.filterOptions.forEach((item: any) => (item.checked = true));
    else this.filterOptions.forEach((item: any) => (item.checked = false));
  }

  /**
   * checkSelectAll determines whether or not the (Select all) option is checked when other selections are made
   * Select all will only be checked when all boxes are checked
   */
  checkSelectAll() {
    for (const item of this.filterOptions)
      if (!item.checked) {
        this.allSelected = false;
        return;
      }

    this.allSelected = true;
  }

  /**
   * resetSearch upon clicking the reset button clears the input field.
   * @returns an empty string.
   */
  resetSearch() {
    this.filterOptions.map((filter: any) => {
      filter.checked = false;
    });
    this.allSelected = false;
    this.searchResults = false;
    this.blankFields = false;
    this.changedFields = false;
    this.invalidFields = false;
    this.searchInput = '';
    this.summaryData = this.originalSummaryData;
  }

  /**
   * loadComplete will fire when the pdf has finished loading the first time
   * @param event is a boolean (true if load is successful)
   */
  loadComplete(event: PDFDocumentProxy) {
    if (event) this.pdfLoadComplete = true;
  }

  /**
   * This function reports the gutter position relative to the left side of the screen.
   * This is used to determine the scale of zooming in and out of the PDF when the gutter is moved.
   * TODO: This process is very slow due to assigning the gutterPosition very frequently (many, many times a second).
   */
  gutterPositionReporting() {
    this.splitEl.dragProgress$.subscribe(gutterPositionArray => {
      this.ngZone.run(() => (this.gutterPositionArray = gutterPositionArray));
      if (
        (typeof this.gutterPositionArray.sizes[0] == 'number' &&
          this.gutterPositionArray.sizes[0] <= 65) ||
        this.gutterPositionArray.sizes[0] >= 35
      )
        this.gutterPositionValue = gutterPositionArray.sizes[0];
    });
  }

  ngOnInit(): void {
    this.extractRouteParameters();
  }

  ngAfterViewInit() {
    this.gutterPositionReporting();
    this.cdRef.detectChanges();
  }

  ngAfterViewChecked(): void {
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.sseObserver?.unsubscribe();
    /* emitStatus as empty should prevent pending audit staying on all the time */
    this.modalService.emitStatus('');
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
